import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Tooltip,
  TablePagination,
  TextField,
  InputAdornment,
} from "@mui/material";
import {
  CircleXIcon,
  Trash2Icon,
  PlayIcon,
  Edit as EditIcon,
  Search as SearchIcon,
  Download as DownloadIcon,
} from "lucide-react";
import { API_BACKEND_URL } from "../../../Config/Config";
import { useDeleteTestCase } from "../../../useQuery";
import { useNotification } from "../../../hooks";
import apiFetch from "../../../utils/apiFetch";
import { getUserDetails } from "../../../utils/userDetailsUtils";
import EditTestCaseModal from "./EditTestCaseModal";
import GenerationMetricsStrip from "./GenerationMetricsStrip";
import ConstructUrl from "./ConstructUrl";
import Deletepopup from "./Common/Deletepopup";
import { exportTestCasesToXLSX } from "../../../utils/exportTestCases";
import { asMap, createScopeResolver, interpolateText } from "../utils/variableInterpolation";
import { useVariables } from "../../../context/VariableContext";

import { getEndpointLabel } from "../utils/endpointLabel";
const TestCaseModal = ({
  open,
  onClose,
  endpoint,
  baseUrl,
  onTestCaseSelect,
  setEndpointResults,
  setChainTest,
  setActiveTab,
  selectedTestCase,
}) => {
  const [testCases, setTestCases] = useState([]);
  const [isRunningAll, setIsRunningAll] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingTestCase, setEditingTestCase] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [isUpdatingTestCase, setIsUpdatingTestCase] = useState(false);
  const [dialogOpen, setDialogopen] = useState(false);
  const [showTitle, setShowTitle] = useState(true);
  const [deleteObj, setDeleteobj] = useState({
    type: "",
    method: {},
    title: "",
  });
  const { interpolate, globalVariables, environmentVariables } = useVariables();
  const notify = useNotification();

  // Delete mutation
  const { mutate: handleDeleteTestCase, isLoading: isDeletingTestCase } = useDeleteTestCase({
    onSuccess: () => {
      notify("Test case deleted successfully", "success");
      fetchTestCases();
    },
    onError: (error) => {
      notify(`Error deleting test case: ${error}`, "error");
    },
  });

  const fetchTestCases = async () => {
    if (!endpoint) return;
    try {
      const response = await apiFetch(`${API_BACKEND_URL}/testcase?endpoint=${endpoint.id}`);
      const data = await response.json();
      setTestCases(data);
    } catch (error) {
      console.error("Error fetching test cases:", error);
      notify("Failed to fetch test cases", "error");
    }
  };

  useEffect(() => {
    if (open) {
      fetchTestCases();
    } else {
      setSearchTerm("");
    }
  }, [open, endpoint]);

  const executeTestCase = async (testCase) => {
    let url = ConstructUrl(baseUrl, testCase);
    const user = getUserDetails();
    const defaultStructure = {};

    const safeJsonParse = (jsonString, fallback = {}) => {
      if (!jsonString || jsonString.trim() === "") return fallback;
      try {
        if (typeof jsonString === "object") return jsonString;
        return JSON.parse(jsonString);
      } catch (error) {
        console.warn("Failed to parse JSON:", error);
        return fallback;
      }
    };

    const endpointVariables = endpoint?.variables || {};

    const tempResolver = createScopeResolver(
      asMap(globalVariables || []),
      asMap(environmentVariables || []),
      endpointVariables,
    );

    const resolvedUrl = interpolateText(url.fullUrl, tempResolver);

    let resolvedHeaders;
    const parsedHeaders = safeJsonParse(testCase.headers, defaultStructure);
    if (typeof parsedHeaders === "object" && parsedHeaders !== null) {
      resolvedHeaders = {};
      Object.entries(parsedHeaders).forEach(([key, value]) => {
        resolvedHeaders[key] = interpolateText(String(value), tempResolver);
      });
    } else {
      resolvedHeaders = interpolateText(String(parsedHeaders), tempResolver);
    }

    let resolvedQueryParams;
    const parsedQueryParams = safeJsonParse(testCase.queryParams, defaultStructure);
    if (typeof parsedQueryParams === "object" && parsedQueryParams !== null) {
      resolvedQueryParams = {};
      Object.entries(parsedQueryParams).forEach(([key, value]) => {
        resolvedQueryParams[key] = interpolateText(String(value), tempResolver);
      });
    } else {
      resolvedQueryParams = interpolateText(String(parsedQueryParams), tempResolver);
    }

    let resolvedPathParams;
    const parsedPathParams = safeJsonParse(testCase.pathParams, defaultStructure);
    if (typeof parsedPathParams === "object" && parsedPathParams !== null) {
      resolvedPathParams = {};
      Object.entries(parsedPathParams).forEach(([key, value]) => {
        resolvedPathParams[key] = interpolateText(String(value), tempResolver);
      });
    } else {
      resolvedPathParams = interpolateText(String(parsedPathParams), tempResolver);
    }

    let finalUrl = resolvedUrl || "";
    if (resolvedPathParams && Object.keys(resolvedPathParams).length > 0) {
      Object.entries(resolvedPathParams).forEach(([key, value]) => {
        finalUrl = finalUrl.replace(new RegExp(`\\{${key}\\}`, "g"), encodeURIComponent(value));
        finalUrl = finalUrl.replace(new RegExp(`:${key}(?=\\/|$)`, "g"), encodeURIComponent(value));
      });
    }

    let resolvedBody = "";
    if (testCase.bodyType === "form-data" && testCase.body) {
      try {
        const parsedBody = safeJsonParse(testCase.body);
        if (parsedBody.fields && Array.isArray(parsedBody.fields)) {
          const processedFields = parsedBody.fields.map((field) => {
            if (field.type === "file") {
              return {
                key: field.key,
                value: field.value || null,
                type: field.type,
                description: field.description || null,
                required: field.required || false,
                src: field.src || null,
                fileName: field.fileName || field.value || null,
                fileSize: field.fileSize || null,
              };
            }
            // Text fields
            return {
              key: field.key,
              value: field.value || null,
              type: field.type,
              description: field.description || null,
              required: field.required || false,
              src: null,
            };
          });
          resolvedBody = JSON.stringify({ fields: processedFields });
        } else {
          resolvedBody = interpolateText(String(testCase.body), tempResolver);
        }
      } catch (error) {
        console.warn("Failed to process form-data body:", error);
        resolvedBody = testCase.body ? interpolateText(String(testCase.body), tempResolver) : "";
      }
    } else {
      resolvedBody = testCase.body ? interpolateText(String(testCase.body), tempResolver) : "";
    }

    try {
      const response = await apiFetch(`${API_BACKEND_URL}/test?companyId=${user.companyid}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          method: testCase.httpMethod,
          url: finalUrl,
          headers: resolvedHeaders || {},
          queryParams: resolvedQueryParams || {},
          pathParams: resolvedPathParams || {},
          body: resolvedBody || "",
          bodyType: testCase.bodyType,
          bodySubType: testCase.bodySubType,
          apiName: testCase.testCase || getEndpointLabel(endpoint),
          id: testCase.id,
          requestKeyValuePairs: {},
          endpointId: endpoint.id,
          expectedStatusCode: testCase.expectedStatusCode,
        }),
      });

      const responseData = await response.json();
      return responseData;
    } catch (error) {
      console.error(`Error executing test case ${testCase.id}:`, error);
      return null;
    }
  };
  const handleRunAllTests = async () => {
    setIsRunningAll(true);
    const results = [];

    try {
      for (const testCase of testCases) {
        try {
          const result = await executeTestCase(testCase);
          results.push({ testCase, result });
        } catch (error) {
          results.push({ testCase, error: error.message || "Test failed" });
        }
      }

      setEndpointResults(results);
      setActiveTab("testResults");
      setChainTest("endpointResults");
      notify("All tests executed successfully", "success");
      onClose();
    } catch (error) {
      console.error("Error running all tests:", error);
      notify("Error running all tests", "error");
    } finally {
      setIsRunningAll(false);
    }
  };

  const handleEditSave = async (updatedTestCase) => {
    setIsUpdatingTestCase(true);
    try {
      const response = await apiFetch(`${API_BACKEND_URL}/testcase/update`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify([[updatedTestCase.id.toString(), updatedTestCase]]),
      });

      if (!response.ok) {
        throw new Error("Failed to update test case");
      }

      notify("Test case updated successfully", "success");
      setEditModalOpen(false);
      fetchTestCases();
    } catch (error) {
      console.error("Error updating test case:", error);
      notify("Failed to update test case", "error");
    } finally {
      setIsUpdatingTestCase(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const filteredTestCases = testCases.filter(
    (testCase) =>
      testCase.testCase?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      testCase.httpMethod?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      testCase.testType?.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const handleSelectTestCase = (testCase) => {
    if (selectedTestCase?.id === testCase.id) {
      // Deselect the test case
      onTestCaseSelect(null);
      onClose();
    } else {
      // Select the test case
      onTestCaseSelect(testCase);
      onClose();
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: "90vh",
            borderRadius: "8px",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "1px solid #e5e7eb",
            padding: "16px 24px",
          }}
        >
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-4">
              <h2 className="text-xl font-apple font-semibold text-gray-900">Test Cases</h2>
              <div className="text-sm text-gray-500 font-apple">
                {endpoint?.path || "No endpoint selected"}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outlined"
                color="primary"
                startIcon={<DownloadIcon size={16} />}
                onClick={() => {
                  console.log(testCases, endpoint);
                  exportTestCasesToXLSX(testCases, endpoint);
                }}
                disabled={testCases.length === 0}
                sx={{
                  textTransform: "none",
                  fontFamily: "inherit",
                  cursor: "pointer",
                  "&.Mui-disabled": {
                    cursor: "not-allowed",
                  },
                }}
              >
                Export to Excel
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<PlayIcon size={16} />}
                onClick={handleRunAllTests}
                disabled={isRunningAll || testCases.length === 0}
                sx={{
                  textTransform: "none",
                  fontFamily: "inherit",
                  cursor: "pointer",
                  "&.Mui-disabled": {
                    cursor: "not-allowed",
                  },
                }}
              >
                {isRunningAll ? "Running..." : `Run All Tests (${testCases.length})`}
              </Button>
              <IconButton onClick={onClose} size="small" sx={{ cursor: "pointer" }}>
                <CircleXIcon className="text-gray-400 hover:text-gray-600" size={20} />
              </IconButton>
            </div>
          </div>
        </DialogTitle>

        <GenerationMetricsStrip endpointId={endpoint?.id} />

        <DialogContent sx={{ padding: "24px" }}>
          <div className="mb-4">
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search test cases..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon size={18} className="text-gray-400" />
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "6px",
                  fontFamily: "inherit",
                },
              }}
            />
          </div>

          <TableContainer component={Paper} sx={{ boxShadow: "none", border: "1px solid #e5e7eb" }}>
            <Table sx={{ minWidth: 650 }} size="medium">
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f9fafb" }}>
                  <TableCell sx={{ fontFamily: "inherit", fontWeight: 600 }}>Test Case</TableCell>
                  <TableCell sx={{ fontFamily: "inherit", fontWeight: 600 }}>Method</TableCell>
                  <TableCell sx={{ fontFamily: "inherit", fontWeight: 600 }}>Type</TableCell>
                  <TableCell sx={{ fontFamily: "inherit", fontWeight: 600 }}>
                    Expected Status
                  </TableCell>
                  <TableCell sx={{ fontFamily: "inherit", fontWeight: 600 }}>Actions</TableCell>
                  <TableCell sx={{ fontFamily: "inherit", fontWeight: 600 }}>Priority</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredTestCases
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((testCase, index) => (
                    <TableRow
                      key={testCase.id}
                      hover
                      onClick={() => handleSelectTestCase(testCase)}
                      sx={{
                        cursor: "pointer !important",
                        "&:last-child td, &:last-child th": { border: 0 },
                        "&:hover": {
                          backgroundColor: "rgba(59, 130, 246, 0.04)",
                        },
                        ...(selectedTestCase?.id === testCase.id && {
                          backgroundColor: "rgba(59, 130, 246, 0.08)",
                          "&:hover": {
                            backgroundColor: "rgba(59, 130, 246, 0.12)",
                          },
                        }),
                      }}
                      className="group"
                      title={
                        showTitle
                          ? selectedTestCase?.id === testCase.id
                            ? "Click to deselect"
                            : "Click to select"
                          : undefined
                      }
                    >
                      <TableCell
                        component="th"
                        scope="row"
                        sx={{
                          fontFamily: "inherit",
                          position: "relative",
                          pl: selectedTestCase?.id === testCase.id ? "20px" : "16px",
                        }}
                      >
                        {selectedTestCase?.id === testCase.id && (
                          <div className="absolute left-2 top-1/2 -translate-y-1/2 w-2 h-2 bg-blue-500 rounded-full" />
                        )}
                        <div className="flex items-center gap-2">
                          <span className="text-gray-900">
                            {testCase.testCase || "No description"}
                          </span>
                          {selectedTestCase?.id === testCase.id && (
                            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                              Selected (click to deselect)
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell sx={{ fontFamily: "inherit" }}>
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            testCase.httpMethod === "GET"
                              ? "bg-green-100 text-green-800"
                              : testCase.httpMethod === "POST"
                                ? "bg-orange-100 text-orange-800"
                                : testCase.httpMethod === "PUT"
                                  ? "bg-blue-100 text-blue-800"
                                  : testCase.httpMethod === "DELETE"
                                    ? "bg-red-100 text-red-800"
                                    : "bg-gray-100 text-gray-800"
                          }`}
                        >
                          {testCase.httpMethod}
                        </span>
                      </TableCell>
                      <TableCell sx={{ fontFamily: "inherit" }}>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          {testCase.testType}
                        </span>
                      </TableCell>
                      <TableCell sx={{ fontFamily: "inherit" }}>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                          {testCase.expectedStatusCode}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Tooltip title="Edit test case">
                            <IconButton
                              onMouseEnter={() => setShowTitle(false)}
                              onMouseLeave={() => setShowTitle(true)}
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingTestCase(testCase);
                                setEditModalOpen(true);
                              }}
                              sx={{ cursor: "pointer" }}
                            >
                              <EditIcon size={16} className="text-blue-500" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete test case">
                            <IconButton
                              size="small"
                              onMouseEnter={() => setShowTitle(false)}
                              onMouseLeave={() => setShowTitle(true)}
                              onClick={(e) => {
                                e.stopPropagation();
                                setDialogopen(true);
                                setDeleteobj({
                                  type: "Delete test case",
                                  method: () => handleDeleteTestCase({ testCaseId: testCase.id }),
                                  title: "Delete Test Case",
                                });
                              }}
                              disabled={isDeletingTestCase}
                              sx={{
                                cursor: "pointer",
                                "&.Mui-disabled": {
                                  cursor: "not-allowed",
                                },
                              }}
                            >
                              <Trash2Icon size={16} className="text-red-500" />
                            </IconButton>
                          </Tooltip>
                        </div>
                      </TableCell>
                      <TableCell sx={{ fontFamily: "inherit" }}>
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            testCase.httpMethod === "P1"
                              ? "bg-green-100 text-green-800"
                              : testCase.httpMethod === "P2"
                                ? "bg-orange-100 text-orange-800"
                                : testCase.httpMethod === "P3"
                                  ? "bg-blue-100 text-blue-800"
                                  : testCase.httpMethod === "P4"
                                    ? "bg-red-100 text-red-800"
                                    : "bg-gray-100 text-gray-800"
                          }`}
                        ></span>
                        {testCase.priority}
                      </TableCell>
                    </TableRow>
                  ))}
                {filteredTestCases.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                      <div className="text-center text-gray-500">
                        {searchTerm ? "No matching test cases found" : "No test cases available"}
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={filteredTestCases.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              ".MuiTablePagination-select": {
                fontFamily: "inherit",
              },
              ".MuiTablePagination-displayedRows": {
                fontFamily: "inherit",
              },
            }}
          />
        </DialogContent>
      </Dialog>

      <EditTestCaseModal
        isOpen={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        testCase={editingTestCase}
        onSave={handleEditSave}
        isLoading={isUpdatingTestCase}
      />
      <Deletepopup
        open={dialogOpen}
        onClose={() => setDialogopen(false)}
        onConfirm={() => {
          deleteObj.method();
          setDialogopen(false);
        }}
        itemName={deleteObj.type}
        title={deleteObj.title}
      />
    </>
  );
};

export default TestCaseModal;
